<template>
  <div
    class="kr-row"
    :class="{ 'kr-row--blur': blurMode }"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <!-- Status dot — hidden during field editing -->
    <span
      v-if="!editingFields"
      class="kr-status-dot"
      :style="{ backgroundColor: statusColor }"
    />

    <!-- Main content: everything inside so title gets full width -->
    <div class="kr-content">
      <!-- Full edit form (replaces normal display) -->
      <div v-if="editingFields" class="kr-field-edit-form">
        <el-input v-model="editFields.title" size="mini" :placeholder="$t('performance.goals.kr_title_placeholder')" class="kr-field-edit-title" />
        <div class="kr-field-edit-metrics">
          <div class="kr-field-edit-pair">
            <div class="kr-field-edit-group">
              <label class="kr-field-edit-group-label">{{ $t("performance.goals.kr_cycle") }}</label>
              <el-select v-model="editFields.cycle" size="mini" filterable allow-create clearable :placeholder="$t('performance.goals.kr_cycle_placeholder')">
                <el-option v-for="c in cycleOptions" :key="c" :label="c" :value="c" />
              </el-select>
            </div>
          </div>
          <div class="kr-field-edit-pair">
            <div class="kr-field-edit-group">
              <label class="kr-field-edit-group-label">{{ $t("performance.goals.kr_start") }}</label>
              <el-input-number v-model="editFields.start_value" size="mini" :controls="false" />
            </div>
            <span class="kr-field-edit-value-arrow">→</span>
            <div class="kr-field-edit-group">
              <label class="kr-field-edit-group-label">{{ $t("performance.goals.kr_target") }}</label>
              <el-input-number v-model="editFields.target_value" size="mini" :controls="false" />
            </div>
            <div class="kr-field-edit-group">
              <label class="kr-field-edit-group-label">{{ $t("performance.goals.kr_unit") }}</label>
              <el-input v-model="editFields.unit" size="mini" placeholder="%" />
            </div>
          </div>
        </div>
        <KrDecreasingWarning v-if="editShowDecreasingIndividualWarning" style="margin-top: 12px;" />
        <div v-if="editShowPerOwnerTargets" class="kr-field-edit-owner-targets">
          <label class="kr-field-edit-group-label">{{ $t('performance.goals.targets_per_owner') }}</label>
          <div class="kr-field-edit-owner-targets-list">
            <div
              v-for="row in editOwnerTargetRows"
              :key="row.email"
              class="kr-field-edit-owner-row"
            >
              <div
                class="kr-field-edit-owner-avatar"
                :style="{
                  backgroundColor: row.house_color || '#ddd',
                  backgroundImage: row.large_pic ? 'url(' + row.large_pic + ')' : 'none',
                }"
              >
                <span v-if="!row.large_pic">{{ getInitials(row.fullname) }}</span>
              </div>
              <span class="kr-field-edit-owner-name" :title="row.fullname">{{ row.fullname }}</span>
              <span v-if="row.customized" class="kr-custom-tag">{{ $t('performance.goals.owner_target_customized_tag') }}</span>
              <el-input-number
                :value="row.target"
                size="mini"
                :min="0"
                :controls="false"
                class="kr-field-edit-owner-input"
                @change="(v) => handleEditOwnerTargetChange(row.email, v)"
              />
              <span v-if="editFields.unit" class="kr-field-edit-owner-unit">{{ editFields.unit }}</span>
              <el-tooltip
                v-if="row.customized"
                :content="$t('performance.goals.owner_target_reset_tooltip')"
                placement="top"
                :open-delay="300"
              >
                <i class="el-icon-refresh-left kr-field-edit-owner-reset" @click="resetEditOwnerTarget(row.email)" />
              </el-tooltip>
              <span v-else class="kr-field-edit-owner-reset-placeholder" />
            </div>
          </div>
          <div
            class="kr-field-edit-rollup"
            :class="{ 'kr-field-edit-rollup--warn': !editTargetSumMatches }"
          >
            <i :class="editTargetSumMatches ? 'el-icon-success' : 'el-icon-warning-outline'" />
            <span>{{ editRollupText }}</span>
            <button
              v-if="!editTargetSumMatches"
              type="button"
              class="kr-field-edit-rollup-action"
              @click="matchEditTeamTargetToSum"
            >
              {{ $t('performance.goals.owner_targets_match_action', { sum: formatDisplayValue(editTargetSum, editFields.unit) }) }}
            </button>
          </div>
        </div>
        <div class="kr-field-edit-footer">
          <el-radio-group
            v-if="keyResult.owner_emails && keyResult.owner_emails.length > 1"
            v-model="editFields.mode"
            size="mini"
            class="kr-field-edit-mode-group"
          >
            <el-radio-button label="collaborative">{{ $t("performance.goals.kr_collaborative") }}</el-radio-button>
            <el-radio-button label="individual">{{ $t("performance.goals.kr_individual") }}</el-radio-button>
          </el-radio-group>
          <div class="kr-field-edit-btns">
            <button class="kr-field-btn kr-field-btn--cancel" @click="cancelFieldEdit">{{ $t("global.Cancel") }}</button>
            <button class="kr-field-btn kr-field-btn--save" @click="saveFieldEdit">{{ $t("global.Save") }}</button>
          </div>
        </div>
      </div>

      <!-- Normal display -->
      <template v-else>
      <!-- Top line: title + cycle tag + status badge + owner avatar.
           Clicking the title (or the line's empty space) opens the KR detail
           dialog — the only place the full, untruncated picture lives. -->
      <div class="kr-top-line" @click.self="openDetail">
        <el-tooltip
          :content="keyResult.title"
          placement="top-start"
          :open-delay="500"
        >
          <span class="kr-title kr-title--clickable" @click="openDetail">
            {{ keyResult.title }}
          </span>
        </el-tooltip>
        <span v-if="keyResult.cycle" class="kr-cycle-tag">{{ keyResult.cycle }}</span>
        <AutomationSourcePill
          v-if="metricSource && metricSource.toolkit_slug"
          :toolkit-slug="metricSource.toolkit_slug"
          :paused="metricSource.enabled === false"
        />
        <el-tooltip
          v-if="metricSource && metricSource.last_error"
          :content="metricSource.last_error.message || 'Source error'"
          placement="top"
        >
          <span class="kr-automation-error-dot" @click.stop="toggleAutomationError" />
        </el-tooltip>
        <!-- Status badge + dropdown shared with the detail dialog. -->
        <KrStatusBadge
          :key-result="keyResult"
          :readonly="readonly"
          @change="handleStatusChange"
        />
        <!-- Owner avatar inline in top line -->
        <el-popover
          v-if="!readonly"
          placement="bottom"
          width="240"
          trigger="manual"
          popper-class="kr-owner-popover"
          v-model="ownerPopoverVisible"
        >
      <div class="kr-owner-picker" @click.stop>
        <el-input
          v-model="ownerSearch"
          size="mini"
          :placeholder="$t('performance.goals.kr_search_team_or_member')"
          prefix-icon="el-icon-search"
          class="kr-owner-search"
          clearable
        />
        <div v-if="filteredTeams.length > 0" class="kr-owner-teams-section">
          <div class="kr-owner-section-label">{{ $t("performance.goals.kr_teams_section") }}</div>
          <div
            v-for="team in filteredTeams"
            :key="'team-' + team.key"
            class="kr-owner-item kr-owner-item--team"
            @click="selectTeamMembers(team.key)"
          >
            <i class="el-icon-office-building" style="color: #909399; font-size: 16px; width: 24px; text-align: center;"></i>
            <span class="kr-owner-item-name">{{ team.name }}</span>
          </div>
          <div class="kr-owner-section-label" style="margin-top: 4px;">{{ $t("performance.goals.kr_people_section") }}</div>
        </div>
        <div class="kr-owner-list">
          <div
            v-for="member in filteredMembers"
            :key="member.email"
            class="kr-owner-item"
            :class="{ 'kr-owner-item--selected': keyResult.owner_emails && keyResult.owner_emails.includes(member.email) }"
            @click="selectOwner(member.email)"
          >
            <div
              class="kr-owner-item-avatar"
              :style="{
                backgroundColor: member.house_color || '#ddd',
                backgroundImage: member.large_pic ? 'url(' + member.large_pic + ')' : 'none',
              }"
            >
              <span v-if="!member.large_pic">{{ getInitials(member.fullname) }}</span>
            </div>
            <span class="kr-owner-item-name">{{ member.fullname }}</span>
            <i v-if="keyResult.owner_emails && keyResult.owner_emails.includes(member.email)" class="el-icon-check kr-owner-item-check" />
          </div>
          <div v-if="filteredMembers.length === 0" class="kr-owner-empty">
            {{ $t("performance.goals.kr_no_members_found") }}
          </div>
        </div>
        <div v-if="keyResult.owner_emails && keyResult.owner_emails.length > 0" class="kr-owner-unassign" @click="clearOwners">
          <i class="el-icon-close" /> {{ $t("performance.goals.kr_clear_all") }}
        </div>
      </div>
      <div slot="reference" :title="ownerTooltip" class="kr-owner-avatars" :class="{ 'kr-owner-avatar--empty': ownersData.length === 0 }" @click.stop="ownerPopoverVisible = !ownerPopoverVisible">
          <template v-if="ownersData.length > 0">
            <div
              v-for="(owner, idx) in ownersData.slice(0, 3)"
              :key="owner.email"
              class="kr-owner-avatar-inner"
              :style="{
                zIndex: 10 - idx,
                backgroundColor: owner.house_color || '#ddd',
                backgroundImage: owner.large_pic ? 'url(' + owner.large_pic + ')' : 'none',
              }"
            >
              <span v-if="!owner.large_pic">{{ getInitials(owner.fullname) }}</span>
            </div>
            <div v-if="ownersData.length > 3" class="kr-owner-avatar-inner kr-owner-more">+{{ ownersData.length - 3 }}</div>
          </template>
          <template v-else>
            <div class="kr-owner-avatar-empty-circle"><i class="el-icon-plus" /></div>
          </template>
        </div>
        </el-popover>
        <!-- Readonly owner display -->
        <el-tooltip v-else-if="ownersData.length > 0" :content="ownerTooltip" placement="top">
          <div class="kr-owner-avatars">
            <div
              v-for="(owner, idx) in ownersData.slice(0, 3)"
              :key="owner.email"
              class="kr-owner-avatar-inner"
              :style="{
                zIndex: 10 - idx,
                backgroundColor: owner.house_color || '#ddd',
                backgroundImage: owner.large_pic ? 'url(' + owner.large_pic + ')' : 'none',
              }"
            >
              <span v-if="!owner.large_pic">{{ getInitials(owner.fullname) }}</span>
            </div>
            <div v-if="ownersData.length > 3" class="kr-owner-avatar-inner kr-owner-more">+{{ ownersData.length - 3 }}</div>
          </div>
        </el-tooltip>
      </div>

      <!-- Bottom line: progress bar + value + actions (or edit inputs) -->
      <div class="kr-bottom-line" @click.self="openDetail">
        <div class="kr-progress-bar">
          <div
            class="kr-progress-bar__fill"
            :style="{ width: progress + '%', backgroundColor: statusColor }"
          />
        </div>
        <div class="kr-progress-text" v-if="!editing">
          {{ progressText }}
        </div>
        <div class="kr-edit-area" v-else>
          <el-input-number
            v-model="editValue"
            size="mini"
            :min="0"
            controls-position="right"
            class="kr-edit-input"
          />
          <el-input
            v-model="editNote"
            size="mini"
            :placeholder="$t('performance.goals.kr_note_placeholder')"
            class="kr-edit-note"
          />
          <i
            class="el-icon-check kr-action-icon kr-action-icon--save"
            @click="saveEdit"
          />
          <i
            class="el-icon-close kr-action-icon kr-action-icon--cancel"
            @click="cancelEdit"
          />
        </div>
        <!-- Action cluster: Update + Automate (promoted) + More overflow.
             Routine ops (Edit fields / Delete) live in the More menu so the
             primary AI action — Automate — reads as the headline affordance. -->
        <el-tooltip v-if="!readonly && actionsVisible && !editing" content="Update progress" placement="top" :open-delay="400">
          <span class="kr-action-icon" @click="startEdit">
            <happily-icon name="edit" :size="16" />
          </span>
        </el-tooltip>
        <el-tooltip v-if="!readonly && actionsVisible && !editing" :content="metricSource ? 'Edit automation' : 'Automate updates'" placement="top" :open-delay="400">
          <button
            type="button"
            class="kr-action-zap"
            :class="{ 'kr-action-zap--active': !!metricSource }"
            @click="openAutomationDialog"
          >
            <happily-icon name="zap" :size="14" />
          </button>
        </el-tooltip>
        <el-dropdown
          v-if="!readonly && actionsVisible && !editing"
          trigger="click"
          placement="bottom-end"
          popper-class="happily-dropdown-popper kr-row-more-popper"
          @command="handleMoreCommand"
          @visible-change="moreOpen = $event"
        >
          <span class="kr-action-icon kr-action-icon--more">
            <happily-icon name="more-horizontal" :size="16" />
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="edit-fields">
              <i class="el-icon-setting" /> Edit key result
            </el-dropdown-item>
            <el-dropdown-item command="delete" divided class="kr-row-more-popper__danger">
              <i class="el-icon-delete" /> Delete
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <!-- Provenance meta line (contract C5): automated KRs show the toolkit
           line, manual KRs show who last updated. Clicking either opens the
           full update timeline (contract C6). -->
      <KrHistoryPopover v-if="hasMetaLine" :key-result="keyResult">
        <div class="kr-automation-meta kr-meta-line" :title="$t('performance.goals.kr_history_title')">
          <template v-if="metricSource && metricSource.toolkit_slug">
            <span v-if="metricSource.enabled === false">Automation paused</span>
            <span v-else-if="metricSource.last_synced_at">
              Updated {{ formatRelativeTime(metricSource.last_synced_at) }} from {{ toolkitDisplayName(metricSource.toolkit_slug) }}
            </span>
            <span v-else-if="metricSource.last_checked_at">
              Last checked {{ formatRelativeTime(metricSource.last_checked_at) }}
            </span>
            <span v-else>Awaiting first sync</span>
          </template>
          <template v-else>
            <span>{{ manualMetaText }}</span>
          </template>
          <i class="el-icon-time kr-meta-line__icon" />
        </div>
      </KrHistoryPopover>

      <!-- Inline error reveal -->
      <div v-if="automationErrorOpen && metricSource && metricSource.last_error" class="kr-automation-error">
        <i class="el-icon-warning-outline" />
        <span class="kr-automation-error__msg">{{ metricSource.last_error.message }}</span>
        <el-button size="mini" type="primary" @click="openAutomationDialog">Reconnect</el-button>
      </div>

      <!-- Individual progress breakdown -->
      <div v-if="isIndividual && individualProgress.length > 1" class="kr-individual-breakdown">
        <div
          v-for="ip in individualProgress"
          :key="ip.email"
          class="kr-individual-row"
          :title="ip.customized ? $t('performance.goals.owner_target_customized_hint') : ''"
        >
          <div
            class="kr-individual-avatar"
            :style="{
              backgroundColor: ip.house_color || '#ddd',
              backgroundImage: ip.large_pic ? 'url(' + ip.large_pic + ')' : 'none',
            }"
          >
            <span v-if="!ip.large_pic">{{ getInitials(ip.fullname) }}</span>
          </div>
          <span class="kr-individual-name">{{ ip.fullname }}</span>
          <div class="kr-individual-bar-container">
            <div class="kr-individual-bar">
              <div
                class="kr-individual-bar-fill"
                :style="{ width: ip.percent + '%', backgroundColor: statusColor }"
              ></div>
            </div>
          </div>
          <span class="kr-individual-value">{{ formatTargetNumber(ip.value) }} / {{ formatTargetNumber(ip.target) }}</span>
          <span v-if="ip.customized" class="kr-custom-tag">{{ $t('performance.goals.owner_target_customized_tag') }}</span>
        </div>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
import { KR_STATUS_COLORS } from '@/constants/krStatus';
import {
  computeOwnerTargetMap,
  computeOwnerTargetRollup,
  freezeOwnerTargetsToEffective,
  formatTargetNumber,
  isDecreasingIndividualKr,
} from '@/store/goals';
import {
  buildOwnerProgressRows,
  formatKrValue,
  formatRelativeKrTime,
  isAutomationActor,
  latestHistoryEntry,
  toolkitDisplayName,
} from '@/store/utils/krUpdateRules';
import KrDecreasingWarning from './KrDecreasingWarning.vue';
import AutomationSourcePill from './AutomationSourcePill.vue';
import KrHistoryPopover from './KrHistoryPopover.vue';
import KrStatusBadge from './KrStatusBadge.vue';

export default {
  name: 'KeyResultRow',
  components: { KrDecreasingWarning, AutomationSourcePill, KrHistoryPopover, KrStatusBadge },
  props: {
    keyResult: { type: Object, required: true },
    goalId: { type: String, required: true },
    teamId: { type: String, required: true },
    readonly: { type: Boolean, default: false },
    blurMode: { type: Boolean, default: false },
    // { krId, token } — set by goals.vue when the detail dialog's
    // "Edit key result" action asks this row to open its field editor.
    // A token bump re-triggers even for the same KR.
    fieldEditRequest: { type: Object, default: null }
  },
  watch: {
    fieldEditRequest(request) {
      if (request && request.krId === this.keyResult.kr_id && !this.readonly) {
        this.startFieldEdit()
      }
    }
  },
  data() {
    return {
      editing: false,
      editValue: 0,
      editNote: '',
      saving: false,
      hovered: false,
      // Tracks the More-overflow dropdown so the action cluster stays
      // visible while the user is interacting with the menu (the popper is
      // teleported to body, which would otherwise un-hover the row).
      moreOpen: false,
      ownerPopoverVisible: false,
      ownerSearch: '',
      editingFields: false,
      editFields: { title: '', type: 'number', start_value: 0, target_value: 100, unit: '', mode: 'collaborative', cycle: '' },
      editOwnerTargets: {},
      automationErrorOpen: false,
    }
  },
  mounted() {
    this._onDocClick = (e) => {
      if (!this.ownerPopoverVisible) return
      const reference = this.$el.querySelector('.kr-owner-avatars')
      if (reference && reference.contains(e.target)) return
      // el-popover keeps all poppers mounted (v-show), so querySelector may
      // return a different row's popper. Check every .kr-owner-popover node.
      const popperEls = document.querySelectorAll('.kr-owner-popover')
      for (let i = 0; i < popperEls.length; i++) {
        if (popperEls[i].contains(e.target)) return
      }
      this.ownerPopoverVisible = false
    }
    document.addEventListener('click', this._onDocClick, true)
  },
  beforeDestroy() {
    if (this._onDocClick) {
      document.removeEventListener('click', this._onDocClick, true)
    }
  },
  computed: {
    // Hover OR an open overflow menu keeps the action cluster visible —
    // when the user clicks "..." the popper teleports to body and the row
    // loses its :hover, but we want the trigger icons to stay put until
    // the menu closes again.
    actionsVisible() {
      return this.hovered || this.moreOpen
    },
    isIndividual() {
      return this.keyResult.mode === 'individual'
    },
    effectiveValue() {
      const kr = this.keyResult
      if (this.isIndividual && kr.owner_progress && Object.keys(kr.owner_progress).length > 0) {
        return Object.values(kr.owner_progress).reduce((sum, v) => sum + v, 0)
      }
      return kr.current_value
    },
    progress() {
      const kr = this.keyResult
      if (kr.target_value === kr.start_value) return 0
      const raw = ((this.effectiveValue - kr.start_value) / (kr.target_value - kr.start_value)) * 100
      return Math.min(Math.max(raw, 0), 100)
    },
    statusColor() {
      return KR_STATUS_COLORS[this.keyResult.status] || '#909399'
    },
    progressText() {
      // formatKrValue owns the value/unit spacing rules ("2 leads", "73%", "$500").
      const kr = this.keyResult
      return formatKrValue(this.effectiveValue, kr.unit) + ' / ' + formatKrValue(kr.target_value, kr.unit)
    },
    cycleOptions() {
      const now = new Date()
      const year = now.getFullYear()
      const quarter = Math.ceil((now.getMonth() + 1) / 3)
      const options = []
      for (let i = 0; i < 4; i++) {
        const q = ((quarter - 1 + i) % 4) + 1
        const y = year + Math.floor((quarter - 1 + i) / 4)
        options.push(`Q${q} ${y}`)
      }
      const h = now.getMonth() < 6 ? 1 : 2
      options.push(`H${h} ${year}`)
      options.push(`${year}`)
      return options
    },
    individualProgress() {
      if (!this.isIndividual) return []
      const kr = this.keyResult
      // Math shared with the detail dialog (buildOwnerProgressRows); only the
      // display resolution (names, avatars) stays here.
      const targetMap = computeOwnerTargetMap(kr.owner_emails, kr.target_value, kr.owner_targets || {})
      return buildOwnerProgressRows(kr, targetMap).map(row => {
        const data = this.$store.getters['goals/resolveOwner'](row.email)
        return {
          ...row,
          fullname: data ? data.fullname : row.email,
          house_color: data ? data.house_color : '#ddd',
          large_pic: data ? data.large_pic : '',
        }
      })
    },
    editShowPerOwnerTargets() {
      const emails = this.keyResult.owner_emails || []
      return this.editFields.mode === 'individual' && emails.length > 1
    },
    editShowDecreasingIndividualWarning() {
      return isDecreasingIndividualKr({
        mode: this.editFields.mode,
        owner_emails: this.keyResult.owner_emails,
        start_value: this.editFields.start_value,
        target_value: this.editFields.target_value,
      })
    },
    editEffectiveOwnerTargets() {
      return computeOwnerTargetMap(this.keyResult.owner_emails, this.editFields.target_value || 0, this.editOwnerTargets)
    },
    editOwnerTargetRows() {
      const emails = this.keyResult.owner_emails || []
      const effective = this.editEffectiveOwnerTargets
      return emails.map((email) => {
        const data = this.$store.getters['goals/resolveOwner'](email)
        return {
          email,
          fullname: data ? data.fullname : email,
          house_color: data ? data.house_color : '#ddd',
          large_pic: data ? data.large_pic : '',
          target: effective[email],
          customized: Object.prototype.hasOwnProperty.call(this.editOwnerTargets, email),
        }
      })
    },
    editTargetRollup() {
      return computeOwnerTargetRollup(this.keyResult.owner_emails, this.editFields.target_value || 0, this.editOwnerTargets)
    },
    editTargetSum() {
      return this.editTargetRollup.sum
    },
    editTargetSumMatches() {
      return this.editTargetRollup.matches
    },
    editRollupText() {
      const rollup = this.editTargetRollup
      const unit = this.editFields.unit || ''
      // Values arrive preformatted (unit spacing handled by formatKrValue) so
      // the i18n templates no longer concatenate {sum}{unit} into "30leads".
      const sum = formatKrValue(rollup.sum, unit)
      const team = formatKrValue(this.editFields.target_value || 0, unit)
      if (rollup.matches) {
        return this.$t('performance.goals.owner_targets_match', { sum, team })
      }
      const delta = formatKrValue(Math.abs(rollup.delta), unit)
      const key = rollup.delta < 0 ? 'performance.goals.owner_targets_short' : 'performance.goals.owner_targets_over'
      return this.$t(key, { sum, team, delta })
    },
    ownersData() {
      const emails = this.keyResult.owner_emails || []
      return emails.map(e => this.$store.getters['goals/resolveOwner'](e)).filter(Boolean)
    },
    ownerTooltip() {
      if (this.ownersData.length === 0) return 'Assign owners'
      return this.ownersData.map(o => o.fullname).join(', ')
    },
    teamMembers() {
      return this.$store.getters['goals/teamMembersForGoal'](this.teamId) || []
    },
    selectableTeams() {
      return this.$store.getters['goals/selectableTeamsForGoal'](this.teamId) || []
    },
    filteredTeams() {
      if (this.selectableTeams.length <= 1) return []
      if (!this.ownerSearch) return this.selectableTeams
      const q = this.ownerSearch.toLowerCase()
      return this.selectableTeams.filter(t => (t.name || '').toLowerCase().includes(q))
    },
    filteredMembers() {
      if (!this.ownerSearch) return this.teamMembers
      const q = this.ownerSearch.toLowerCase()
      return this.teamMembers.filter(m => m.fullname.toLowerCase().includes(q))
    },
    metricSource() {
      return this.keyResult.metric_source || null
    },
    hasMetaLine() {
      if (this.metricSource && this.metricSource.toolkit_slug) return true
      const history = this.keyResult.progress_history
      return Array.isArray(history) && history.length > 0
    },
    manualMetaText() {
      const entry = latestHistoryEntry(this.keyResult.progress_history)
      if (!entry) return ''
      const time = formatRelativeKrTime(entry.updated_at)
      if (isAutomationActor(entry.updated_by)) {
        // 'system:automation' must never render as a person's name (C5).
        return this.$t('performance.goals.kr_meta_updated_auto', { time })
      }
      if (entry.updated_by) {
        const actor = this.$store.getters['goals/resolveOwner'](entry.updated_by)
        const name = actor && actor.fullname ? actor.fullname.split(/\s+/)[0] : entry.updated_by.split('@')[0]
        return this.$t('performance.goals.kr_meta_updated_by', { time, name })
      }
      return this.$t('performance.goals.kr_meta_updated', { time })
    },
  },
  methods: {
    handleMoreCommand(command) {
      if (command === 'edit-fields') this.startFieldEdit()
      else if (command === 'delete') this.handleDelete()
    },
    startEdit() {
      this.editValue = this.keyResult.current_value
      this.editNote = ''
      this.editing = true
    },
    cancelEdit() {
      this.editing = false
      this.editNote = ''
    },
    saveEdit() {
      this.$emit('update-progress', {
        krId: this.keyResult.kr_id,
        newValue: this.editValue,
        note: this.editNote
      })
      this.editing = false
    },
    handleStatusChange(status) {
      this.$emit('update-status', {
        krId: this.keyResult.kr_id,
        status: status
      })
    },
    handleDelete() {
      this.$emit('delete-kr', {
        krId: this.keyResult.kr_id
      })
    },
    getInitials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return (parts[0][0] || '?').toUpperCase()
    },
    openDetail() {
      // Detail view must open even in readonly mode — visibility is the point.
      this.$emit('open-detail', {
        krId: this.keyResult.kr_id,
        goalId: this.goalId,
        teamId: this.teamId,
      })
    },
    formatDisplayValue(value, unit) {
      return formatKrValue(value, unit)
    },
    startFieldEdit() {
      const kr = this.keyResult
      this.editFields = {
        title: kr.title,
        type: kr.type,
        start_value: kr.start_value,
        target_value: kr.target_value,
        unit: kr.unit || '',
        mode: kr.mode || 'collaborative',
        cycle: kr.cycle || '',
      }
      this.editOwnerTargets = { ...(kr.owner_targets || {}) }
      this.editingFields = true
    },
    cancelFieldEdit() {
      this.editingFields = false
      this.editOwnerTargets = {}
    },
    saveFieldEdit() {
      const fields = { ...this.editFields }
      const emails = this.keyResult.owner_emails || []
      if (fields.mode === 'individual' && emails.length > 1) {
        fields.owner_targets = { ...this.editOwnerTargets }
      } else {
        // Drop any stored per-owner overrides when we exit individual mode.
        fields.owner_targets = {}
      }
      this.$emit('update-fields', {
        krId: this.keyResult.kr_id,
        fields,
      })
      this.editingFields = false
      this.editOwnerTargets = {}
    },
    handleEditOwnerTargetChange(email, value) {
      const emails = this.keyResult.owner_emails || []
      if (!emails.includes(email)) return
      const num = Number(value)
      const safe = Number.isFinite(num) ? num : 0
      this.$set(this.editOwnerTargets, email, safe)
    },
    resetEditOwnerTarget(email) {
      if (email in this.editOwnerTargets) {
        const next = { ...this.editOwnerTargets }
        delete next[email]
        this.editOwnerTargets = next
      }
    },
    matchEditTeamTargetToSum() {
      const sum = this.editTargetSum
      if (!Number.isFinite(sum)) return
      // Freeze displayed values before bumping team target so uncustomized rows don't reflow.
      this.editOwnerTargets = freezeOwnerTargetsToEffective(
        this.keyResult.owner_emails,
        this.editOwnerTargets,
        this.editEffectiveOwnerTargets,
      )
      this.$set(this.editFields, 'target_value', Number.isInteger(sum) ? sum : Math.round(sum * 100) / 100)
    },
    formatTargetNumber(n) {
      return formatTargetNumber(n)
    },
    selectTeamMembers(teamKey) {
      const members = this.$store.getters['goals/teamMembersForGoal'](teamKey) || []
      const current = new Set(this.keyResult.owner_emails || [])
      for (const m of members) current.add(m.email)
      this.$emit('update-owner', { krId: this.keyResult.kr_id, ownerEmails: [...current] })
      this.$nextTick(() => { this.ownerPopoverVisible = true })
    },
    selectOwner(email) {
      const current = this.keyResult.owner_emails || []
      let updated
      if (current.includes(email)) {
        // Remove (toggle off)
        updated = current.filter(e => e !== email)
      } else {
        // Add
        updated = [...current, email]
      }
      this.$emit('update-owner', { krId: this.keyResult.kr_id, ownerEmails: updated })
      this.$nextTick(() => { this.ownerPopoverVisible = true })
    },
    clearOwners() {
      this.ownerPopoverVisible = false
      this.ownerSearch = ''
      this.$emit('update-owner', { krId: this.keyResult.kr_id, ownerEmails: [] })
    },
    openAutomationDialog() {
      this.$emit('open-automation', { krId: this.keyResult.kr_id })
    },
    toggleAutomationError() {
      this.automationErrorOpen = !this.automationErrorOpen
    },
    toolkitDisplayName(slug) {
      return toolkitDisplayName(slug)
    },
    formatRelativeTime(value) {
      return formatRelativeKrTime(value)
    },
  }
}
</script>

<style scoped>
.kr-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: white;
  transition: background-color 0.15s;
}

.kr-row:hover {
  background-color: #fafafa;
}

.kr-row--blur {
  filter: blur(5px);
  pointer-events: none;
  user-select: none;
}

.kr-status-dot {
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  display: inline-block;
}

.kr-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kr-top-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kr-bottom-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kr-title {
  font-size: 13px;
  font-weight: 500;
  color: #4A4A4A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.kr-progress-bar {
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  flex: 1;
  min-width: 60px;
  overflow: hidden;
}

.kr-progress-bar__fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.kr-progress-text {
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  white-space: nowrap;
  flex-shrink: 0;
}

.kr-title--clickable {
  cursor: pointer;
  transition: color 0.15s;
}

.kr-title--clickable:hover {
  color: #6C30CC;
}

/* Empty space in these lines is a click target for the detail dialog. */
.kr-top-line,
.kr-bottom-line {
  cursor: pointer;
}

.kr-action-icon {
  font-size: 14px;
  color: #909399;
  cursor: pointer;
  transition: color 0.3s;
  flex-shrink: 0;
}

.kr-action-icon:hover {
  color: #6C30CC;
}

/* Promoted automation chip. Always renders as a brand-tinted button — not a
   plain icon — so it reads as the headline AI affordance in the cluster.
   When automation is already configured (`--active`), it shifts to a solid
   brand fill: signals "this KR is wired up, click to edit" without needing
   a separate badge. */
.kr-action-zap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: #f2ecfb;
  color: #6c30cc;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 160ms ease, color 160ms ease,
              transform 160ms ease, box-shadow 160ms ease;
}

.kr-action-zap:hover {
  background: #e6dafa;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(108, 48, 204, 0.18);
}

.kr-action-zap:active {
  transform: translateY(0);
  box-shadow: none;
}

.kr-action-zap--active {
  background: #6c30cc;
  color: #ffffff;
}

.kr-action-zap--active:hover {
  background: #522698;
  box-shadow: 0 2px 6px rgba(108, 48, 204, 0.35);
}

/* Overflow trigger inherits the neutral .kr-action-icon look — it's a
   secondary affordance that should defer to Update + Automate. */
.kr-action-icon--more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: 6px;
  outline: none;
}

.kr-action-icon--more:hover {
  background: #f5f5fa;
}

.kr-action-icon--save {
  color: #3AC47D;
}

.kr-action-icon--save:hover {
  color: #2a9d5e;
}

.kr-action-icon--cancel {
  color: #F44336;
}

.kr-action-icon--cancel:hover {
  color: #d32f2f;
}

/* Field edit form */
.kr-field-edit-form {
  padding: 2px 0;
}

.kr-field-edit-title {
  width: 100%;
  margin-bottom: 10px;
}

/* Normalize all inputs to same height */
.kr-field-edit-form .el-input__inner,
.kr-field-edit-form .el-input-number .el-input__inner {
  height: 32px;
  line-height: 32px;
}

.kr-field-edit-metrics {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.kr-field-edit-pair {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.kr-field-edit-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.kr-field-edit-group-label {
  font-size: 10px;
  font-weight: 600;
  color: #909399;
  letter-spacing: 0.3px;
  margin-bottom: 3px;
}

.kr-field-edit-value-arrow {
  font-size: 14px;
  color: #BDBDBD;
  padding-bottom: 6px;
  flex-shrink: 0;
}

.kr-field-edit-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.kr-field-edit-btns {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.kr-field-btn {
  font-family: 'Lato', 'Kanit', sans-serif;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 14px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}

.kr-field-btn--cancel {
  background: white;
  border: 1px solid #DCDFE6;
  color: #747474;
}

.kr-field-btn--cancel:hover {
  border-color: #C0C4CC;
}

.kr-field-btn--save {
  background: #6C30CC;
  color: white;
}

.kr-field-btn--save:hover {
  background: #5a28a8;
}

/* Cycle tag */
.kr-cycle-tag {
  font-size: 10px;
  font-weight: 600;
  color: #909399;
  background: #f4f4f4;
  padding: 1px 6px;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Individual progress breakdown */
.kr-individual-breakdown {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kr-individual-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 6px;
}

/* Muted uppercase tag marking an owner whose target differs from the
   default share (replaces the old purple left-border accent). */
.kr-custom-tag {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8a8a;
  flex-shrink: 0;
}

.kr-individual-avatar {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.kr-individual-name {
  font-size: 11px;
  font-weight: 500;
  color: #909399;
  width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}

.kr-individual-bar-container {
  flex: 1;
  min-width: 0;
}

.kr-individual-bar {
  height: 3px;
  background: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
}

.kr-individual-bar-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.kr-individual-value {
  font-size: 11px;
  font-weight: 700;
  color: #747474;
  min-width: 30px;
  text-align: right;
  flex-shrink: 0;
  white-space: nowrap;
}

/* Per-owner targets editor (inside field edit form) */
.kr-field-edit-owner-targets {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.kr-field-edit-owner-targets-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
  padding: 8px 10px;
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 6px;
}

.kr-field-edit-owner-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 4px 2px 6px;
}

.kr-field-edit-owner-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.kr-field-edit-owner-name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  color: #4A4A4A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kr-field-edit-owner-input {
  width: 88px;
  flex-shrink: 0;
}

.kr-field-edit-owner-input .el-input__inner {
  text-align: center;
  padding: 0 8px;
}

.kr-field-edit-owner-unit {
  font-size: 11px;
  font-weight: 600;
  color: #909399;
  min-width: 18px;
  flex-shrink: 0;
}

.kr-field-edit-owner-reset,
.kr-field-edit-owner-reset-placeholder {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kr-field-edit-owner-reset {
  font-size: 14px;
  color: #909399;
  cursor: pointer;
  transition: color 0.15s;
}

.kr-field-edit-owner-reset:hover {
  color: #6C30CC;
}

.kr-field-edit-rollup {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #3AC47D;
}

.kr-field-edit-rollup--warn {
  color: #F7B924;
}

.kr-field-edit-rollup-action {
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  color: #6C30CC;
  background: transparent;
  border: none;
  padding: 0 2px;
  margin-left: 4px;
  cursor: pointer;
  text-decoration: underline;
}

.kr-field-edit-rollup-action:hover {
  color: #5a28a8;
}

.kr-field-edit-mode-group {
  display: flex;
}

.kr-field-edit-mode-group .el-radio-button__inner {
  font-size: 11px !important;
  padding: 5px 12px !important;
}

.kr-field-edit-mode-group .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background-color: #6C30CC !important;
  border-color: #6C30CC !important;
  box-shadow: -1px 0 0 0 #6C30CC !important;
  color: #fff !important;
}

.kr-edit-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.kr-edit-input {
  width: 120px;
}

.kr-edit-note {
  width: 140px;
}

/* Owner avatars */
.kr-owner-avatars {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: box-shadow 0.2s;
  border-radius: 12px;
  padding: 1px;
}

.kr-owner-avatars:hover {
  box-shadow: 0 0 0 2px rgba(108, 48, 204, 0.15);
}

.kr-owner-avatar--empty {
  cursor: pointer;
}

.kr-owner-avatar-empty-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px dashed #BDBDBD;
  color: #BDBDBD;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, color 0.2s;
}

.kr-owner-avatar--empty:hover .kr-owner-avatar-empty-circle {
  border-color: #6C30CC;
  color: #6C30CC;
}

.kr-owner-avatar-inner {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: white;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-left: -6px;
  flex-shrink: 0;
}

.kr-owner-avatar-inner:first-child {
  margin-left: 0;
}

.kr-owner-more {
  background: #909399;
  font-size: 9px;
}

/* Owner picker popover */
.kr-owner-search {
  margin-bottom: 8px;
}

.kr-owner-list {
  max-height: 200px;
  overflow-y: auto;
}

.kr-owner-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.kr-owner-item:hover {
  background-color: #f5f5f5;
}

.kr-owner-item--selected {
  background-color: rgba(108, 48, 204, 0.06);
}

.kr-owner-item-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.kr-owner-item-name {
  font-size: 13px;
  font-weight: 500;
  color: #4A4A4A;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kr-owner-item-check {
  color: #6C30CC;
  font-size: 14px;
  flex-shrink: 0;
}

.kr-owner-empty {
  padding: 12px 8px;
  font-size: 12px;
  color: #909399;
  text-align: center;
}

.kr-owner-section-label {
  font-size: 11px;
  color: #909399;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 8px 2px;
}

.kr-owner-item--team {
  background-color: rgba(108, 48, 204, 0.03);
}

.kr-owner-item--team:hover {
  background-color: rgba(108, 48, 204, 0.08);
}

.kr-owner-unassign {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 8px 0;
  margin-top: 8px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
  color: #F44336;
  cursor: pointer;
  transition: color 0.15s;
}

.kr-owner-unassign:hover {
  color: #d32f2f;
}

/* Provenance meta line (automation or manual) + error reveal */
.kr-automation-meta {
  font-size: 11px;
  font-weight: 500;
  color: #8a8a8a;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The meta line doubles as the update-history trigger (contract C6). */
.kr-meta-line {
  cursor: pointer;
  transition: color 0.15s;
}

.kr-meta-line:hover {
  color: #6c30cc;
}

.kr-meta-line__icon {
  font-size: 11px;
  margin-left: 4px;
  opacity: 0.7;
}

.kr-automation-error-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f7b924;
  margin-left: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.kr-automation-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 8px 12px;
  background: #fff8ec;
  border: 1px solid #f5dfa2;
  border-radius: 8px;
  font-size: 12px;
  color: #4a4a4a;
}

.kr-automation-error__msg {
  flex: 1;
}
</style>

<!-- Global styles for the el-dropdown popper, which Element UI teleports to
     body and so escapes scoped CSS. Targets only this row's overflow menu via
     `kr-row-more-popper`. -->
<style>
.kr-row-more-popper .el-dropdown-menu__item i {
  margin-right: 8px;
  font-size: 14px;
  color: #8a8a8a;
}

.kr-row-more-popper .el-dropdown-menu__item:hover i {
  color: #6c30cc;
}

.kr-row-more-popper__danger,
.kr-row-more-popper .kr-row-more-popper__danger {
  color: #cc3030 !important;
}

.kr-row-more-popper .kr-row-more-popper__danger:hover {
  background: #fef0f0 !important;
  color: #cc3030 !important;
}

.kr-row-more-popper .kr-row-more-popper__danger:hover i {
  color: #cc3030 !important;
}
</style>
